import React, { useState, useEffect, useCallback } from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { useTranslation } from "react-i18next";
import { AWS_URL } from "../../../components/config/constants";
import Loader from "../../../components/Loader";
import { ImPlay3 } from "react-icons/im";
import { IoCheckmarkSharp } from "react-icons/io5";
import CollectionService from "../../../services/CollectionService";
import EmptyPlaceHolderCollection from "../../../components/common/EmptyPlaceHolderCollection.jsx";
import InfiniteScroll from "react-infinite-scroll-component";
import CustomAudioPlayer from "../../../components/common/CustomAudioPlayer.jsx";
import CustomFancybox from "../../../components/common/CustomFancyBox.jsx";
import moment from "moment";

const CollectionModal = ({
  selectedMediaType: propSelectedMediaType,
  hideAllMedia: propHideAllMedia,
  hideImages: propHideImages,
  hideVideos: propHideVideos,
  hideAudios: propHideAudios,
  hideVr: propHideVr,
  showCollectionModal,
  handleCloseModal,
  handleAddMedia,
}) => {
  const { t } = useTranslation();

  const [selectedMediaType, setSelectedMediaType] = useState(propSelectedMediaType || "All");
  const [collectionList, setCollectionList] = useState([]);
  const [totalCollections, setTotalCollections] = useState(0);
  const [allMediaCollection, setAllMediaCollection] = useState({ mediasList: [] });
  const [allMedia, setAllMedia] = useState({ mediasList: [] });
  const [mediaIds, setMediaIds] = useState([]);
  const [showListOfCollection, setShowListOfCollection] = useState(true);
  const [showCollectionDetails, setShowCollectionDetails] = useState(false);
  const [collectionId, setCollectionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [collectionsPage, setCollectionsPage] = useState(0);
  const [page, setPage] = useState(0);
  const [limit] = useState(18);
  const [showFancyBox, setShowFancyBox] = useState(false);
  const [current, setCurrent] = useState(0);
  const [hideAllMedia] = useState(propHideAllMedia || false);
  const [hideImages] = useState(propHideImages || false);
  const [hideVideos] = useState(propHideVideos || false);
  const [hideAudios] = useState(propHideAudios || false);
  const [hideVr] = useState(propHideVr || false);

  const getAllCollection = useCallback(() => {
    setLoading(true);
    let params = {
      limit: 4,
      page: 0,
      mediaType: selectedMediaType,
    };

    CollectionService.getAllCollection({ ...params })
      .then((res) => {
        setAllMediaCollection(res.data.data);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
      });
  }, [selectedMediaType]);

  const getUserColletion = useCallback(() => {
    setLoading(true);
    let params = {
      page: collectionsPage,
    };

    CollectionService.listUserCollection({ ...params })
      .then((res) => {
        setCollectionList((prev) => [
          ...prev,
          ...res.data.data.collectionsList,
        ]);
        setTotalCollections(res.data.data.total);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
      });
  }, [collectionsPage]);

  useEffect(() => {
    getUserColletion();
    getAllCollection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateMediaIds = (media) => {
    let newMedia = {
      _id: media._id,
      url: media.url,
      dataURL: media.url,
      name: media.mediaName,
      isVaultMedia: true,
      isEdit: true,
      ...(media.isVR && { isVR: true, projectionType: media.projectionType }),
      typeOfMedia: media.mediaType,
      type: media.mediaType,
      blurredThumbNailImage: media.mediaBlurredThumbNailName,
      thumbNailImage: media.mediaThumbNailName,
      uploadedMediaDataUrl: media.url,
      uploadedMedia: {
        isEdit: true,
        name: media.mediaName,
        type: media.mediaType,
        uploadedMediaDataUrl: media.url,
        url: media.url,
      },
    };
    const indexElement = mediaIds.findIndex(
      (element) => element._id === newMedia._id
    );
    if (selectedMediaType === "vr") {
      if (indexElement !== -1) {
        setMediaIds([]);
      } else {
        setMediaIds([newMedia]);
      }
    } else {
      if (indexElement !== -1) {
        const updated = [...mediaIds];
        updated.splice(indexElement, 1);
        setMediaIds(updated);
      } else {
        setMediaIds([...mediaIds, newMedia]);
      }
    }
  };

  const getCollectionDetails = useCallback(() => {
    setLoading(true);
    let params = {
      limit: limit,
      page: page,
      collectionId: collectionId,
      mediaType: selectedMediaType,
    };

    CollectionService.getCollectionDetails({ ...params })
      .then((res) => {
        let newAllMedia = res.data.data;
        newAllMedia.mediasList = [
          ...allMedia.mediasList,
          ...res.data.data.mediasList,
        ];
        setAllMedia(newAllMedia);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
      });
  }, [limit, page, collectionId, selectedMediaType, allMedia.mediasList]);

  const getAllMedias = useCallback(() => {
    setLoading(true);
    let params = {
      limit: limit,
      page: page,
      mediaType: selectedMediaType,
    };

    CollectionService.getAllCollection({ ...params })
      .then((res) => {
        let newAllMedia = res.data.data;
        newAllMedia.mediasList = [
          ...allMedia.mediasList,
          ...res.data.data.mediasList,
        ];
        setAllMedia(newAllMedia);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
      });
  }, [limit, page, selectedMediaType, allMedia.mediasList]);

  // Track refs for triggering fetches after state updates
  const mediaTypeChangeRef = React.useRef(null);

  const handleMediaTypeChange = (type) => {
    const clearIds = selectedMediaType === "vr" && type !== "vr";
    if (clearIds) {
      setMediaIds([]);
    }
    setSelectedMediaType(type);
    setAllMedia({ mediasList: [] });
    mediaTypeChangeRef.current = type;
  };

  // Effect to fetch after media type change resets allMedia
  useEffect(() => {
    if (mediaTypeChangeRef.current !== null && allMedia.mediasList.length === 0) {
      if (collectionId) {
        getCollectionDetails();
      } else {
        getAllMedias();
      }
      mediaTypeChangeRef.current = null;
    }
  }, [allMedia, collectionId, getCollectionDetails, getAllMedias]);

  const width = window.innerWidth;
  const isMobile = width <= 512;

  return (
    <>
      {loading && <Loader />}
      <Modal
        show={showCollectionModal}
        onHide={() => {
          handleCloseModal();
          setShowCollectionDetails(false);
          setShowListOfCollection(true);
          setAllMedia({ mediasList: [] });
        }}
        keyboard={false}
        centered
        className="vaultModel"
      >
        <Modal.Header className="text-black " closeButton>
          <span className={`font-xss`}>
            {t("collection.selectMediaFromCollection")}
          </span>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: "70vh" }} className="text-black">
          <CustomFancybox
            medias={allMedia.mediasList.map((media) => {
              let newMedia = media;
              media.type = media.mediaType;
              media.thumbNailUrl = media.thumbnailUrl;
              return newMedia;
            })}
            show={showFancyBox}
            current={current}
            t={t}
            hide={() => {
              setShowFancyBox(false);
            }}
            place={"postMedias"}
          />
          <div
            id="scrollCollectionsModal"
            style={{ height: "calc(70vh - 2rem)", overflowY: "scroll" }}
          >
            <InfiniteScroll
              dataLength={
                showCollectionDetails === true
                  ? allMedia.mediasList.length
                  : collectionList?.length
              }
              next={() => {
                if (showCollectionDetails === true) {
                  setPage((prev) => prev + 1);
                } else {
                  setCollectionsPage((prev) => prev + 1);
                }
              }}
              hasMore={
                showCollectionDetails === true
                  ? allMedia?.mediasList?.length <
                    allMedia.allMediasCount
                  : collectionList?.length <
                    totalCollections
                  ? true
                  : false
              }
              scrollableTarget="scrollCollectionsModal"
              className="right-scroll-bar"
            >
              {showCollectionDetails === true && (
                <div className="mb-3 newVault">
                  <div
                    className={` mb-2 fw-600 font-xsss vaultText text-black`}
                  >
                    {t("collection.selectMediaType")}
                  </div>
                  <div className="p-2 vaultTab">
                    {!hideAllMedia && (
                      <span
                        className={`badge badge-pill pointer vaultText  ${
                          selectedMediaType === "All"
                            ? "bg-blue text-white"
                            : " bg-gray text-black"
                        }  fw-600 me-3 font-xsss`}
                        onClick={() => handleMediaTypeChange("All")}
                      >
                        {t("common.allMedia")}
                      </span>
                    )}
                    {!hideImages && (
                      <span
                        className={`badge badge-pill pointer vaultText ${
                          selectedMediaType === "image"
                            ? "bg-blue text-white"
                            : " bg-gray text-black"
                        } text-black fw-600 me-3 font-xsss`}
                        onClick={() => handleMediaTypeChange("image")}
                      >
                        {t("common.images")}
                      </span>
                    )}
                    {!hideVideos && (
                      <span
                        className={`badge badge-pill pointer vaultText ${
                          selectedMediaType === "video"
                            ? "bg-blue text-white"
                            : " bg-gray text-black"
                        } text-black fw-600 me-3 font-xsss`}
                        onClick={() => handleMediaTypeChange("video")}
                      >
                        {t("bookmark.videos")}
                      </span>
                    )}
                    {!hideAudios && (
                      <span
                        className={`badge badge-pill pointer vaultText ${
                          selectedMediaType === "audio"
                            ? "bg-blue text-white"
                            : " bg-gray text-black"
                        } text-black fw-600 me-3 font-xsss`}
                        onClick={() => handleMediaTypeChange("audio")}
                      >
                        {t("bookmark.audios")}
                      </span>
                    )}
                    {!hideVr && (
                      <span
                        className={`badge badge-pill pointer vaultText ${
                          selectedMediaType === "vr"
                            ? "bg-blue text-white"
                            : " bg-gray text-black"
                        } text-black fw-600 me-3 font-xsss`}
                        onClick={() => handleMediaTypeChange("vr")}
                      >
                        {t("home.vr")}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {showCollectionDetails && (
                <div className="container-media-parent">
                  {allMedia.mediasList.map((media, index) => (
                    <span
                      onClick={() => {
                        if (!media.type?.includes("audio")) {
                          setShowFancyBox(true);
                          setCurrent(index);
                        }
                      }}
                      className="container-media-collection-sub2 hover"
                    >
                      <div className="media-collection-date">
                        <p>
                          {moment(media.createdAt)
                            .locale(localStorage.getItem("i18nextLng"))
                            .format("MMM D, YYYY")}
                        </p>
                      </div>
                      {mediaIds.findIndex(
                        (element) => element._id === media._id
                      ) !== -1 ? (
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                            updateMediaIds(media);
                          }}
                          className="select-media-collection-selected"
                        >
                          <span
                            style={{ bottom: "3px", position: "relative" }}
                          >
                            <IoCheckmarkSharp
                              style={{
                                color: "white",
                                fontSize: "10px",
                                position: "relative",
                                top: "-6px",
                                left: "4px",
                              }}
                            ></IoCheckmarkSharp>
                          </span>
                        </div>
                      ) : (
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                            updateMediaIds(media);
                          }}
                          className="select-media-collection"
                        ></div>
                      )}
                      {media.mediaType?.includes("image") && (
                        <img
                          className="image-colletion"
                          alt=""
                          loading="lazy"
                          src={`${media.url}`}
                        ></img>
                      )}
                      {media.mediaType?.includes("audio") && (
                        <div>
                          <img
                            className="image-colletion icon-fix-collection"
                            src={AWS_URL + "logo/audio.png"}
                            alt=""
                            loading="lazy"
                          ></img>
                          <div className="audio-text-collection">
                            <div>{media.mediaName.slice(13, 33)}</div>

                            <div
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              <div style={{ maxHeight: "35px" }}>
                                <CustomAudioPlayer
                                  wid={"0px"}
                                  CustomStyle={{
                                    maxHeight: "35px",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    width: "100%",
                                  }}
                                  src={media.url}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      {media.mediaType?.includes("video") && (
                        <>
                          <img
                            className="image-colletion"
                            alt=""
                            loading="lazy"
                            src={`${media.thumbnailUrl}`}
                          ></img>
                          <div className="containter-button-mobile-pause-profile">
                            <div className="pause-button-clip">
                              <div className="pause-internal-button-clip">
                                <ImPlay3
                                  style={{
                                    width: "36px",
                                    height: "36px",
                                    color: "#FFFFFF",
                                    paddingLeft: "4px",
                                  }}
                                ></ImPlay3>
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </span>
                  ))}
                </div>
              )}

              {showListOfCollection === true && (
                <div className="vault-page-container row px-4">
                  <div className="col-lg-4" style={{ marginBottom: "24px" }}>
                    <div
                      onClick={() => {
                        setShowListOfCollection(false);
                        setShowCollectionDetails(true);
                        // getAllMedias will be triggered via effect
                        setTimeout(() => getAllMedias(), 0);
                      }}
                      className="image-card hover"
                    >
                      <div>
                        <div className="image-collection">
                          {allMediaCollection.mediasList.map(
                            (media, index) => (
                              <>
                                {media.mediaType.includes("image") && (
                                  <div className="div-image-colletion">
                                    <img
                                      className="image-colletion"
                                      src={media.url}
                                      alt=""
                                      loading="lazy"
                                    ></img>
                                  </div>
                                )}
                                {media.mediaType.includes("video") && (
                                  <div className="div-image-colletion">
                                    <img
                                      className="image-colletion"
                                      src={media.thumbnailUrl}
                                      alt=""
                                      loading="lazy"
                                    ></img>
                                  </div>
                                )}
                                {media.mediaType.includes("audio") && (
                                  <div className="div-image-colletion">
                                    <img
                                      className="image-colletion"
                                      src={AWS_URL + "logo/audio.png"}
                                      alt=""
                                      loading="lazy"
                                    ></img>
                                  </div>
                                )}
                              </>
                            )
                          )}
                          {allMediaCollection.mediasList.length <
                            4 && (
                            <EmptyPlaceHolderCollection></EmptyPlaceHolderCollection>
                          )}
                          {allMediaCollection.mediasList.length <
                            3 && (
                            <EmptyPlaceHolderCollection></EmptyPlaceHolderCollection>
                          )}
                          {allMediaCollection.mediasList.length <
                            2 && (
                            <EmptyPlaceHolderCollection></EmptyPlaceHolderCollection>
                          )}
                          {allMediaCollection.mediasList.length <
                            1 && (
                            <EmptyPlaceHolderCollection></EmptyPlaceHolderCollection>
                          )}
                        </div>
                        <div className="collection-name py-3 text-color-dark bgc-white fw-bold">
                          {t("collection.all")}{" "}
                          {allMediaCollection?.mediasList
                            ?.length < 1 ? (
                            <small className="collection-name-empty">
                              ({t("common.empty")})
                            </small>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>
                  {collectionList.map((collection, index) => (
                    <div
                      className="col-lg-4"
                      style={{ marginBottom: "24px" }}
                    >
                      <div
                        onClick={() => {
                          setShowListOfCollection(false);
                          setShowCollectionDetails(true);
                          setCollectionId(collection._id);
                          // getCollectionDetails will be triggered via effect or setTimeout
                          setTimeout(() => getCollectionDetails(), 0);
                        }}
                        className="image-card hover"
                      >
                        <div
                          to={{
                            pathname: `/collection-details`,
                            state: {
                              id: collection._id,
                              name: collection.name,
                            },
                          }}
                        >
                          <div className="image-collection">
                            {collection.thumbnails.map((media, index) => (
                              <>
                                {media?.mediaType.includes("image") && (
                                  <div className="div-image-colletion">
                                    <img
                                      className="image-colletion"
                                      src={media.url}
                                      alt=""
                                      loading="lazy"
                                    ></img>
                                  </div>
                                )}
                                {media?.mediaType.includes("video") && (
                                  <div className="div-image-colletion">
                                    <img
                                      className="image-colletion"
                                      src={media.url}
                                      alt=""
                                      loading="lazy"
                                    ></img>
                                  </div>
                                )}
                                {media?.mediaType.includes("audio") && (
                                  <div className="div-image-colletion">
                                    <img
                                      className="image-colletion"
                                      src={AWS_URL + "logo/audio.png"}
                                      alt=""
                                      loading="lazy"
                                    ></img>
                                  </div>
                                )}
                              </>
                            ))}
                            {collection.thumbnails.length < 4 && (
                              <EmptyPlaceHolderCollection></EmptyPlaceHolderCollection>
                            )}
                            {collection.thumbnails.length < 3 && (
                              <EmptyPlaceHolderCollection></EmptyPlaceHolderCollection>
                            )}
                            {collection.thumbnails.length < 2 && (
                              <EmptyPlaceHolderCollection></EmptyPlaceHolderCollection>
                            )}
                            {collection.thumbnails.length < 1 && (
                              <EmptyPlaceHolderCollection></EmptyPlaceHolderCollection>
                            )}
                          </div>
                          <div className="collection-name py-3 text-color-dark bgc-white fw-bold">
                            {collection.name}{" "}
                            {collection.thumbnails.length < 1 ? (
                              <small className="collection-name-empty">
                                ({t("common.empty")})
                              </small>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}{" "}
                </div>
              )}
            </InfiniteScroll>
          </div>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-end">
          <Button
            variant="link"
            className={`text-decoration-none pointer fw-600 ${
              isMobile ? " font-xsss" : " font-xsss"
            } add-new-category text-silver`}
            onClick={() => {
              handleCloseModal();
              setShowCollectionDetails(false);
              setShowListOfCollection(true);
              setAllMedia({ mediasList: [] });
            }}
          >
            {t("common.cancel")}
          </Button>
          <Button
            variant="link"
            className={`text-decoration-none pointer fw-600 ${
              isMobile ? " font-xsss" : " font-xsss"
            } add-new-category ${
              !(mediaIds.length > 0) && " text-muted"
            }`}
            onClick={() => {
              handleCloseModal();
              handleAddMedia(mediaIds);
              setShowCollectionDetails(false);
              setShowListOfCollection(true);
              setMediaIds([]);
            }}
            disabled={!(mediaIds.length > 0)}
            style={{ color: "#4ECC6A" }}
          >
            {t("collection.simpleAdd")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CollectionModal;
